import React, { useCallback, useContext, useMemo, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Box } from '@mui/material';

import { LocaleSelectContext } from 'layouts/LocaleProvider/LocaleProvider';
import {
  Header,
  AppointmentsCalendar,
  CreateCalendarEvent,
  EditCalendarEvent,
  AppointmentsCalendarContainerProps,
  CalendarEventCreateParams,
  CalendarEventEditParams,
} from 'features';

import { TimeHelper } from '../../utils';
import { Service } from '../../types';

import styles from './index.module.scss';
import { CalendarOverview } from '../../features/CalendarOverview';
import { Reports } from '../../features/Reports';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentStore } from '../../store/store/selectors';

export function Calendar() {
  const store = useAppSelector(selectCurrentStore);

  const [editAppointmentData, setEditAppointmentData] = useState<CalendarEventEditParams | undefined>(undefined);
  const [appointmentCreateData, setAppointmentCreateData] = useState<CalendarEventCreateParams | null>(null);
  const [showCalendarOverview, setShowCalendarOverview] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [date, setDate] = useState(TimeHelper.toDayjs(Date.now(), store.timezone).startOf('day'));
  const localeContext = useContext(LocaleSelectContext);

  const isReportEnabled = useMemo(() => !!(store.settings.slotBalancerSettings && store.settings.slotBalancerSettings.enabled), [store]);

  const onDateChange = useCallback((value: string) => {
    setDate(TimeHelper.toDayjs(value, store.timezone));
  }, []);

  const onFormOpen: AppointmentsCalendarContainerProps['onAppointmentClick'] = useCallback(({ calendarEvent, isPastEvent }) => {
    setEditAppointmentData({
      appointmentId: calendarEvent.id,
      type: calendarEvent.type,
      employeeId: calendarEvent.suggestedEmployee,
      isPastEvent,
    });
  }, []);

  const onFormClose = useCallback(() => {
    setEditAppointmentData(undefined);
  }, []);

  const onCopyToNewAppointment = useCallback((params: CalendarEventCreateParams) => {
    setAppointmentCreateData(params);
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={localeContext?.locale || 'en'}>
      <Box className={styles.calendarBoxWrapper}>
        <Header />
        <div className={styles.content}>
          <AppointmentsCalendar
            date={date}
            onDateChange={onDateChange}
            onAppointmentClick={onFormOpen}
            onCreateAppointmentClick={(params?: {
              services: Service[];
              time: number;
              date: string;
              employeeId?: string;
              isSplit?: boolean;
            }) => {
              setAppointmentCreateData(params || { date: TimeHelper.toStandardFormat(date) });
            }}
            onShowCalendarOverviewClick={() => setShowCalendarOverview(true)}
            isReportEnabled={isReportEnabled}
            onShowReportClick={() => setShowReport(true)}
          />
          {showCalendarOverview && <CalendarOverview date={date.valueOf()} onClose={() => setShowCalendarOverview(false)} />}
          {showReport && <Reports onClose={() => setShowReport(false)} />}
        </div>

        {editAppointmentData && (
          <EditCalendarEvent
            {...editAppointmentData}
            onClose={onFormClose}
            shouldReload={false}
            onCopyToNew={onCopyToNewAppointment}
          />
        )}
        {appointmentCreateData && <CreateCalendarEvent onClose={() => setAppointmentCreateData(null)} params={appointmentCreateData} />}
      </Box>
    </LocalizationProvider>
  );
}
